
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/context/AuthContext';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Mail, AlertTriangle, CheckCircle, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { AffiliateReferralBanner } from '@/components/affiliate/AffiliateReferralBanner';
import { useAnalytics } from '@/hooks/useAnalytics';

const RegisterPage = () => {
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [whatsappError, setWhatsappError] = useState('');
  
  const { register, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { track } = useAnalytics();
  const [signupStartTracked, setSignupStartTracked] = React.useState(false);
  const markStarted = () => { if (!signupStartTracked) { track("signup_started"); setSignupStartTracked(true); } };

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const validateWhatsApp = (whatsapp: string) => {
    // Remove todos os caracteres não numéricos
    const numbersOnly = whatsapp.replace(/\D/g, '');
    
    // Verifica se tem pelo menos 10 dígitos (número brasileiro)
    if (numbersOnly.length < 10) {
      return false;
    }
    
    // Verifica se é um número brasileiro válido
    if (numbersOnly.length === 11) {
      // Celular com DDD (deve começar com 9 após o DDD)
      const ddd = numbersOnly.substring(0, 2);
      const firstDigit = numbersOnly.substring(2, 3);
      return parseInt(ddd) >= 11 && parseInt(ddd) <= 99 && firstDigit === '9';
    } else if (numbersOnly.length === 10) {
      // Fixo com DDD
      const ddd = numbersOnly.substring(0, 2);
      return parseInt(ddd) >= 11 && parseInt(ddd) <= 99;
    } else if (numbersOnly.length === 13 && numbersOnly.startsWith('55')) {
      // Com código do país
      const ddd = numbersOnly.substring(2, 4);
      const firstDigit = numbersOnly.substring(4, 5);
      return parseInt(ddd) >= 11 && parseInt(ddd) <= 99 && firstDigit === '9';
    }
    
    return false;
  };

  const formatWhatsApp = (value: string) => {
    // Remove todos os caracteres não numéricos
    const numbersOnly = value.replace(/\D/g, '');
    
    // Aplica formatação
    if (numbersOnly.length <= 2) {
      return numbersOnly;
    } else if (numbersOnly.length <= 7) {
      return `(${numbersOnly.slice(0, 2)}) ${numbersOnly.slice(2)}`;
    } else if (numbersOnly.length <= 11) {
      return `(${numbersOnly.slice(0, 2)}) ${numbersOnly.slice(2, 7)}-${numbersOnly.slice(7)}`;
    } else {
      return `+${numbersOnly.slice(0, 2)} (${numbersOnly.slice(2, 4)}) ${numbersOnly.slice(4, 9)}-${numbersOnly.slice(9, 13)}`;
    }
  };

  const handleWhatsAppChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatWhatsApp(e.target.value);
    setWhatsapp(formatted);
    
    // Validação em tempo real
    if (formatted && !validateWhatsApp(formatted)) {
      setWhatsappError('WhatsApp deve ter formato válido (ex: (11) 99999-9999)');
    } else {
      setWhatsappError('');
    }
  };

  const buildWhatsappUrl = (userName: string) => {
    const phone = '5573981988263';
    const message = `Olá! Sou ${userName} e acabei de me cadastrar no IA PALPITE GURU. Preciso de ajuda para ativar meu plano:`;
    return `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
  };

  const redirectToWhatsApp = (userName: string) => {
    const url = buildWhatsappUrl(userName);
    // Tenta abrir em nova aba (dentro do user-gesture). Se for bloqueado, faz redirect no mesmo tab.
    const win = window.open(url, '_blank');
    if (!win || win.closed || typeof win.closed === 'undefined') {
      window.location.href = url;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!firstName.trim()) {
      toast.error('Nome é obrigatório');
      return;
    }

    if (!email.trim()) {
      toast.error('Email é obrigatório');
      return;
    }

    if (!whatsapp.trim()) {
      toast.error('WhatsApp é obrigatório');
      return;
    }

    if (!validateWhatsApp(whatsapp)) {
      setWhatsappError('WhatsApp deve ter formato válido (ex: (11) 99999-9999)');
      toast.error('WhatsApp deve ter um formato válido');
      return;
    }

    if (password.length < 6) {
      toast.error('Senha deve ter pelo menos 6 caracteres');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Senhas não coincidem');
      return;
    }

    setIsLoading(true);

    try {
      console.log('Iniciando cadastro com dados:', {
        firstName: firstName.trim(),
        email: email.trim(),
        phone: phone.trim(),
        whatsapp: whatsapp.trim()
      });

      const registerResult = await register(firstName.trim(), email.trim(), phone.trim(), password, whatsapp.trim());
      console.log('Resultado do registro:', registerResult);
      
      const { error, outcome } = registerResult;

      if (error) {
        console.error('Erro no cadastro:', error);
        if (error.message.includes('User already registered')) {
          toast.error('Este email já está cadastrado. Tente fazer login.');
          setTimeout(() => navigate('/login'), 2000);
        } else if (error.message.includes('Invalid email')) {
          toast.error('Email inválido');
        } else if (error.message.includes('Password should be at least 6 characters')) {
          toast.error('Senha deve ter pelo menos 6 caracteres');
        } else {
          toast.error('Erro ao criar conta: ' + error.message);
        }
      } else {
        // Tratar diferentes outcomes
        console.log('Registration outcome:', outcome);
        console.log('Tipo do outcome:', typeof outcome);
        
        if (outcome === 'new_user') {
          console.log('✅ NOVO USUÁRIO - Exibindo tela de confirmação de e-mail');
          setRegistrationSuccess(true);
          toast.success('Conta criada! Confirme seu e-mail para ativar o acesso.');
          // Redirecionamento automático DESATIVADO: o usuário decide clicar no botão do WhatsApp.
        } else if (outcome === 'resent_confirmation') {
          console.log('Email de confirmação reenviado');
          setRegistrationSuccess(true);
          toast.success('Reenviamos o e-mail de confirmação. Verifique sua caixa de entrada e o SPAM.');
        } else if (outcome === 'already_confirmed') {
          console.log('Usuário já confirmou email');
          toast.error('Este email já está cadastrado e confirmado. Faça login.');
          setTimeout(() => navigate('/login'), 2000);
        } else {
          // Fallback para casos não identificados - assumir novo usuário
          console.log('Cadastro processado - assumindo novo usuário');
          setRegistrationSuccess(true);
          toast.success('Conta criada! Confirme seu e-mail para ativar o acesso.');
        }

      }
    } catch (error) {
      console.error('Registration error:', error);
      toast.error('Erro inesperado ao criar conta');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoToLogin = () => {
    navigate('/login');
  };

  // Redirecionamento automático para o WhatsApp DESATIVADO (prejudicava a confirmação de e-mail).
  // Código mantido apenas como referência; o contato agora acontece por clique do usuário no CTA.
  // React.useEffect(() => {
  //   if (registrationSuccess && firstName) {
  //     const t = setTimeout(() => {
  //       const url = buildWhatsappUrl(firstName.trim());
  //       const win = window.open(url, '_blank');
  //       if (!win) window.location.href = url;
  //     }, 6000);
  //     return () => clearTimeout(t);
  //   }
  // }, [registrationSuccess, firstName]);


  const [resending, setResending] = React.useState(false);
  const handleResendConfirmation = async () => {
    if (!email) return;
    setResending(true);
    try {
      const { supabase } = await import('@/integrations/supabase/client');
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email.trim().toLowerCase(),
        options: { emailRedirectTo: `${window.location.origin}/auth/callback` },
      });
      if (error) throw error;
      toast.success('E-mail de confirmação reenviado! Verifique sua caixa de entrada e SPAM.');
    } catch (err: any) {
      toast.error(err?.message || 'Não foi possível reenviar agora.');
    } finally {
      setResending(false);
    }
  };


  if (registrationSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 px-4 py-6">
        <div className="w-full max-w-lg space-y-4">
          <AffiliateReferralBanner />
          <Card className="w-full mx-auto shadow-xl">
            <CardHeader className="text-center pb-6">
              <div className="mx-auto w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6 animate-scale-in">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <CardTitle className="text-2xl md:text-3xl font-bold text-green-700 mb-2">
                Email Enviado!
              </CardTitle>
              <CardDescription className="text-lg text-gray-600">
                Verifique sua caixa de entrada
              </CardDescription>
            </CardHeader>
            
             <CardContent className="space-y-4 sm:space-y-6 px-3 sm:px-4 md:px-6">
               {/* Informações do E-mail de Confirmação */}
               <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-6 shadow-inner">
                 <h3 className="font-bold text-blue-800 mb-4 text-lg flex items-center gap-2">
                   📧 Informações do E-mail de Confirmação
                 </h3>
                 <div className="space-y-3 text-blue-700">
                   <div className="bg-white rounded-lg p-3 border border-blue-100">
                     <p className="text-sm"><strong>Remetente:</strong> Supabase Auth</p>
                   </div>
                   <div className="bg-white rounded-lg p-3 border border-blue-100">
                     <p className="text-sm"><strong>Título:</strong> IA PALPITE GURU, IMPORTANTE: Confirme seu Email!</p>
                   </div>
                   <div className="bg-white rounded-lg p-3 border border-blue-100">
                     <p className="text-sm"><strong>Enviado para:</strong> <span className="font-mono">{email}</span></p>
                   </div>
                 </div>
               </div>

                {/* Banner WhatsApp Support */}
                <div className="relative bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-400 rounded-2xl p-4 sm:p-5 shadow-lg overflow-hidden">
                  <div className="absolute inset-0 pointer-events-none rounded-2xl ring-2 ring-green-400/30 animate-pulse" />
                  
                  <div className="relative flex items-center gap-3 mb-3">
                    <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center shadow-md shrink-0">
                      <MessageCircle className="w-6 h-6 sm:w-7 sm:h-7 text-white" strokeWidth={2.5} />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-bold text-green-900 text-base sm:text-lg leading-tight">
                        Quer ativar seu plano agora?
                      </h3>
                      <p className="text-green-700 text-xs sm:text-sm leading-tight">
                        Fale com a nossa equipe no WhatsApp
                      </p>
                    </div>
                  </div>
                  
                  <p className="relative text-green-800 text-xs sm:text-sm leading-relaxed mb-4">
                    Tiramos suas dúvidas e liberamos o pagamento na hora. Você fica nesta página — só sai se clicar abaixo.
                  </p>
                  
                  <Button
                    onClick={() => redirectToWhatsApp(firstName)}
                    className="relative w-full min-h-[60px] sm:min-h-[68px] h-auto py-3.5 px-4 text-sm sm:text-base font-extrabold bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white shadow-xl shadow-green-500/25 animate-pulse rounded-xl whitespace-normal"
                  >
                    <span className="flex items-center justify-center gap-2 sm:gap-3 text-center leading-tight">
                      <MessageCircle className="w-6 h-6 sm:w-7 sm:h-7 shrink-0" strokeWidth={2.5} />
                      <span className="break-words">Falar no WhatsApp e ativar meu plano</span>
                    </span>
                  </Button>
                  
                  <p className="relative text-xs sm:text-sm text-green-700 mt-3 text-center font-medium">
                    👆 Toque no botão verde — atendimento humano, resposta rápida
                  </p>
                </div>


               {/* Próximos Passos */}
               <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-xl p-6 shadow-inner">
                 <h3 className="font-bold text-emerald-800 mb-3 text-lg flex items-center gap-2">
                   🎯 Próximos Passos
                 </h3>
                 <ol className="space-y-3 text-emerald-700">
                   <li className="flex items-start gap-3">
                     <span className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold mt-0.5 shadow-md">1</span>
                     <span className="font-medium">Verifique sua caixa de entrada de e-mail</span>
                   </li>
                   <li className="flex items-start gap-3">
                     <span className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold mt-0.5 shadow-md">2</span>
                     <span className="font-medium">Clique no link de confirmação do Supabase Auth</span>
                   </li>
                    <li className="flex items-start gap-3">
                      <span className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold mt-0.5 shadow-md">3</span>
                      <span className="font-medium">Acesse a plataforma e comece a usar!</span>
                    </li>
                 </ol>
               </div>
               
               {/* Aviso sobre Spam */}
               <div className="bg-gradient-to-br from-yellow-50 to-orange-50 border-2 border-yellow-200 rounded-xl p-6 shadow-inner">
                 <h3 className="font-bold text-yellow-800 mb-2 text-lg flex items-center gap-2">
                   ⚠️ Não Encontrou o E-mail?
                 </h3>
                 <div className="text-yellow-700 text-sm leading-relaxed space-y-2">
                   <p>• Verifique a pasta de <strong>spam</strong> ou <strong>lixo eletrônico</strong></p>
                   <p>• Procure por e-mails do remetente <strong>"Supabase Auth"</strong></p>
                   <p>• Adicione nosso endereço à sua lista de contatos confiáveis</p>
                 </div>
                </div>

               {/* Seção do botão */}
               <div className="text-center pt-4 space-y-4">
                 <p className="text-gray-600 text-sm md:text-base">
                   Após confirmar seu e-mail, você pode fazer login
                 </p>
                  <Button 
                    onClick={handleGoToLogin} 
                    className="w-full h-12 text-base font-semibold bg-blue-600 hover:bg-blue-700 transition-colors shadow-lg"
                  >
                    ✅ Já confirmei meu e-mail — Entrar
                  </Button>
                  <Button
                    onClick={handleResendConfirmation}
                    disabled={resending}
                    variant="outline"
                    className="w-full h-11 font-semibold border-2 border-yellow-400 text-yellow-800 hover:bg-yellow-50"
                  >
                    {resending ? 'Reenviando...' : '📧 Reenviar e-mail de confirmação'}
                  </Button>
               </div>
             </CardContent>
           </Card>
         </div>
       </div>
     );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 px-3 py-4">
      <div className="w-full max-w-md space-y-4">
        <AffiliateReferralBanner />
        
        <Card className="max-h-[95vh] overflow-hidden">
          <CardHeader className="text-center pb-4">
            <CardTitle className="text-xl md:text-2xl font-bold">Criar Conta</CardTitle>
            <CardDescription className="text-sm">
              Cadastre-se para acessar nossa plataforma
            </CardDescription>
          </CardHeader>
          <CardContent className="max-h-[calc(95vh-120px)] overflow-y-auto px-4">
          <form onSubmit={handleSubmit} onFocus={markStarted} className="space-y-3">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium mb-1">
                Nome Completo *
              </label>
              <Input
                id="firstName"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Digite seu nome completo"
                required
                className="h-9"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium mb-1">
                Email *
              </label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu@email.com"
                required
                className="h-9"
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium mb-1">
                Telefone
              </label>
              <Input
                id="phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(11) 1234-5678"
                className="h-9"
              />
            </div>

            <div>
              <label htmlFor="whatsapp" className="block text-sm font-medium mb-1">
                WhatsApp * <span className="text-red-500">(Obrigatório)</span>
              </label>
              <Input
                id="whatsapp"
                type="tel"
                value={whatsapp}
                onChange={handleWhatsAppChange}
                placeholder="(11) 99999-9999"
                required
                className={`h-9 ${whatsappError ? 'border-red-500 border-2 shadow-red-100' : 'border-2 border-green-300 focus:border-green-500'}`}
              />
              {whatsappError && (
                <p className="text-xs text-red-600 mt-1 font-medium">
                  {whatsappError}
                </p>
              )}
              {!whatsappError && whatsapp && (
                <p className="text-xs text-green-600 mt-1">
                  📱 Necessário para suporte e comunicações importantes
                </p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium mb-1">
                Senha *
              </label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  required
                  className="h-9 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium mb-1">
                Confirmar Senha *
              </label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Digite a senha novamente"
                  required
                  className="h-9 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2"
                >
                  {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div className="pt-2">
              <Button 
                type="submit" 
                className="w-full h-10" 
                disabled={isLoading}
              >
                {isLoading ? 'Criando conta...' : 'Criar Conta'}
              </Button>
            </div>
          </form>

          <div className="mt-4 text-center pb-4">
            <p className="text-sm text-muted-foreground">
              Já tem uma conta?{' '}
              <Link to="/login" className="text-primary hover:underline">
                Fazer login
              </Link>
            </p>
          </div>
        </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RegisterPage;
